import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserEvents, createSpeakerInvitation } from '../../lib/speakerService';

const InviteSpeakerModal = ({ speaker, isOpen, onClose }) => {
  const { user, isChurchAdmin, isAdmin } = useAuth();
  const [userEvents, setUserEvents] = useState([]);
  const [, setLoadingEvents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    eventId: '',
    conferenceName: '',
    conferenceDates: '',
    location: '',
    expectedAttendance: '',
    ministryType: '',
    message: '',
  });

  useEffect(() => {
    if (isOpen && user && (isChurchAdmin || isAdmin)) {
      setLoadingEvents(true);
      getUserEvents(user.id).then((events) => {
        setUserEvents(events);
        setLoadingEvents(false);
      });
    }
  }, [isOpen, user, isChurchAdmin, isAdmin]);

  if (!isOpen) return null;

  const isOrganizer = isChurchAdmin || isAdmin;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEventSelect = (e) => {
    const eventId = e.target.value;
    const selected = userEvents.find((ev) => ev.id === eventId);
    setForm({
      ...form,
      eventId,
      conferenceName: selected?.title || form.conferenceName,
      conferenceDates: selected ? `${selected.start_date} to ${selected.end_date || selected.start_date}` : form.conferenceDates,
      location: selected?.location || selected?.venue_name || form.location,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Try to save invitation to database
      const { error: inviteError } = await createSpeakerInvitation({
        speaker_id: speaker.id,
        event_id: form.eventId || null,
        conference_name: form.conferenceName,
        conference_dates: form.conferenceDates,
        location: form.location,
        expected_attendance: form.expectedAttendance ? parseInt(form.expectedAttendance) : null,
        ministry_type: form.ministryType,
        message: form.message,
      });

      // If table doesn't exist, fall back to email
      if (inviteError && inviteError.message?.includes('relation') && inviteError.message?.includes('does not exist')) {
        // Fallback to mailto
        const subject = `Speaking Invitation: ${form.conferenceName || 'Your Conference'}`;
        const body = `Dear ${speaker.name},

I would like to invite you to speak at ${form.conferenceName || 'our conference'}.

Conference Details:
${form.conferenceDates ? `Dates: ${form.conferenceDates}\n` : ''}${form.location ? `Location: ${form.location}\n` : ''}${form.expectedAttendance ? `Expected Attendance: ${form.expectedAttendance}\n` : ''}${form.ministryType ? `Type of Ministry: ${form.ministryType}\n` : ''}

Message:
${form.message}

Thank you for your consideration.`;

        window.location.href = `mailto:${speaker.email || 'noreply@confplanners.com'}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setSuccess(true);
      } else if (inviteError) {
        throw inviteError;
      } else {
        setSuccess(true);
      }
    } catch (err) {
      console.error('Invitation error:', err);
      setError('Could not send invitation. Please try again or contact the speaker directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setForm({ eventId: '', conferenceName: '', conferenceDates: '', location: '', expectedAttendance: '', ministryType: '', message: '' });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Invite to Speak</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success ? (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Invitation Sent!</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              Your invitation has been sent to {speaker.name}.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Speaker info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                {speaker.name?.charAt(0) || '?'}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{speaker.name}</p>
                {speaker.title && <p className="text-xs text-gray-500 dark:text-gray-400">{speaker.title}</p>}
              </div>
            </div>

            {/* Select existing event (organizers only) */}
            {isOrganizer && userEvents.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Your Conference (optional)
                </label>
                <select
                  value={form.eventId}
                  onChange={handleEventSelect}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
                >
                  <option value="">— Or fill in manually —</option>
                  {userEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Conference name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Conference Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="conferenceName"
                required
                value={form.conferenceName}
                onChange={handleChange}
                placeholder="e.g., Missouri Men's Conference 2027"
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Dates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Conference Dates
              </label>
              <input
                type="text"
                name="conferenceDates"
                value={form.conferenceDates}
                onChange={handleChange}
                placeholder="e.g., September 17–19, 2027"
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="e.g., St. Louis, Missouri"
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Expected attendance + ministry type */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expected Attendance
                </label>
                <input
                  type="number"
                  name="expectedAttendance"
                  value={form.expectedAttendance}
                  onChange={handleChange}
                  placeholder="e.g., 200"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ministry Type
                </label>
                <input
                  type="text"
                  name="ministryType"
                  value={form.ministryType}
                  onChange={handleChange}
                  placeholder="e.g., Keynote"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                required
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder={`We would love to invite you to speak at our conference...`}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteSpeakerModal;
